import React from "react";
import {
  Links,
  Meta,
  Scripts,
  ScrollRestoration,
  useLocation,
} from "react-router";
import { AuthProvider } from "./context/AuthContext";
import { ModulePermissionsProvider } from "./context/ModulePermissionsContext";
import { NotchProvider } from "./context/NotchContext";
import { TaskPaletteProvider } from "./context/TaskPaletteContext";
import { WorkerListProvider } from "./context/WorkerListContext";
import { WorkersListGateProvider } from "./services/api/worker-ani/queries/WorkersListGate";
import { PodListProvider } from "./context/PodListContext";
import { QueryProvider } from "./providers/QueryProvider";
import { ColorSchemeProvider } from "./context/ColorSchemeContext";
import { AuthRedirects } from "./components/auth/AuthRedirects";
import {
  DEV_HTTP_SECURITY_HEADERS,
  HTTP_SECURITY_HEADERS,
} from "./security/httpSecurityHeaders";
import type { Route } from "./+types/root";

// Import theme initialization script
import "./utils/themeInit";
import { hideScrollbarsInEdge } from "./utils/edgeScrollbarHider";

import "./app.css";
import { POPPINS_YANTRAMANAV_GOOGLE_FONTS_URL } from "@/fonts/reportBuilderFonts";

export { default as ErrorBoundary } from "./components/error/ErrorBoundary";

export const meta = () => {
  return [
    { title: "Ani Tech" },
    { name: "description", content: "Ani Tech Platform" },
  ];
};

export const headers: Route.HeadersFunction = () => ({
  ...(import.meta.env.DEV ? DEV_HTTP_SECURITY_HEADERS : HTTP_SECURITY_HEADERS),
});

export const links: Route.LinksFunction = () => [
  { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Golos+Text:wght@400;500;600;700&display=swap",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Caveat:wght@400..700&display=swap",
  },
  {
    rel: "stylesheet",
    href: POPPINS_YANTRAMANAV_GOOGLE_FONTS_URL,
  },
];

export const Layout = ({ children }: { children: React.ReactNode }) => {
  // Hide scrollbars only in Microsoft Edge
  React.useEffect(() => {
    hideScrollbarsInEdge();
  }, []);

  return (
    <html lang="en" className="h-full" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){try{var t=localStorage.getItem('theme');if(t==='light'){document.documentElement.classList.remove('dark');}else{document.documentElement.classList.add('dark');}}catch(e){document.documentElement.classList.add('dark');}})();`,
          }}
        />
        <Meta />
        <Links />
      </head>
      <body className="h-full bg-white dark:bg-slate-700">
        <NotchProvider>
          <TaskPaletteProvider>{children}</TaskPaletteProvider>
        </NotchProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
};

export const App = () => {
  // Use React Router's useLocation hook for SSR-safe route checking
  const location = useLocation();
  const isPdfRoute = location.pathname === "/pdf";
  const isUploadRoute = location.pathname.startsWith("/upload/");
  const isBookingLinkRoute = location.pathname.startsWith("/book/");

  if (isPdfRoute || isUploadRoute || isBookingLinkRoute) {
    // Bypass all providers for PDF, magic upload and booking link routes - they manage their own context
    return <AuthRedirects />;
  }

  return (
    <ColorSchemeProvider>
      <QueryProvider>
        <AuthProvider>
          <ModulePermissionsProvider>
            <WorkersListGateProvider>
              <WorkerListProvider>
                <PodListProvider>
                  <AuthRedirects />
                </PodListProvider>
              </WorkerListProvider>
            </WorkersListGateProvider>
          </ModulePermissionsProvider>
        </AuthProvider>
      </QueryProvider>
    </ColorSchemeProvider>
  );
};

export default App;
